/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import {
  Row, Col, Card, Flex, Space, Select, InputNumber,
  Button, Table, Typography, message, Tag,
} from "antd";
import { SearchOutlined, ClearOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { TbFileExport } from "react-icons/tb";
import { materialColumn } from "./model";
import OptionService from "../../../../service/ERP/Options.service";
import ReportService from "../../../../service/ERP/Report.service";

const opService = OptionService();
const reportservice = ReportService();

const TYPE_OPTIONS = [
  { value: "PG", label: "PG (Packing Goods)", typename: "Packing Goods" },
  { value: "FG", label: "FG (Finished Goods)", typename: "Finished Goods" },
  { value: "WI", label: "WI (Work Instruction)", typename: "Work Instruction" },
];

const typenameOf = (ptype) =>
  TYPE_OPTIONS.find((t) => t.value === ptype)?.typename;

const CalculateByProduct = () => {
  const [materials, setMaterials] = useState([]);   // ตัวเลือกสินค้าทั้งหมด (PG/FG/WI)
  const [items, setItems] = useState([
    { key: 1, ptype: "PG", code: null, qty: null },
  ]);
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState([]);
  const [resultDataPK, setResultDataPK] = useState([]);
  const [isCalculated, setIsCalculated] = useState(false);

  useEffect(() => {
    opService
      .optionsMaterial({})
      .then((res) => {
        const { data } = res.data;
        setMaterials(
          (data || []).filter((d) =>
            ["Packing Goods", "Finished Goods", "Work Instruction"].includes(d.typename)
          )
        );
      })
      .catch(() => message.error("โหลดรายการสินค้าไม่สำเร็จ"));
  }, []);

  const optionsByType = useMemo(() => {
    const map = { PG: [], FG: [], WI: [] };
    materials.forEach((m) => {
      const key =
        m.typename === "Packing Goods" ? "PG"
        : m.typename === "Finished Goods" ? "FG"
        : "WI";
      map[key].push({
        value: m.mat_code,
        label: `${m.mat_code} : ${m.mat_name}`,
        unit: m.unit,
      });
    });
    return map;
  }, [materials]);

  const updateItem = (key, field, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.key === key
          ? { ...it, [field]: value, ...(field === "ptype" ? { code: null } : {}) }
          : it
      )
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { key: (prev[prev.length - 1]?.key || 0) + 1, ptype: "PG", code: null, qty: null },
    ]);
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((it) => it.key !== key));
  };

  const handleClear = () => {
    setItems([{ key: 1, ptype: "PG", code: null, qty: null }]);
    setResultData([]);
    setResultDataPK([]);
    setIsCalculated(false);
  };

  const handleCal = () => {
    const valid = items.filter((it) => it.code && Number(it.qty) > 0);
    if (valid.length === 0) {
      message.warning("กรุณาเลือกสินค้าและกรอกจำนวนที่ต้องการผลิต");
      return;
    }
    setLoading(true);
    reportservice
      .getCalculateMaterialByProduct({
        items: valid.map((it) => ({ ptype: it.ptype, code: it.code, qty: it.qty })),
      })
      .then((res) => {
        const { rawmat, pkmat } = res.data;
        setResultData(
          (rawmat || []).filter(
            (item) =>
              item.typename !== "Finish Goods" &&
              item.typename !== "Finished Goods" &&
              item.typename !== "Work Instruction" &&
              item?.typename !== "Packing" &&
              item?.typename !== "Packaging" &&
              item?.typename !== ""
          )
        );
        setResultDataPK(pkmat || []);
        setIsCalculated(true);
      })
      .catch((err) => {
        console.log(err);
        message.error(err?.response?.data?.message || "Request error!");
        setIsCalculated(false);
      })
      .finally(() => setLoading(false));
  };

  const handleExport = async (rows) => {
    if (!rows || rows.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }
    try {
      await reportservice.getCalculateMaterialDownload({ data: rows });
    } catch (error) {
      console.error("Error exporting:", error);
    }
  };

  const matcolumn = materialColumn({});

  const itemColumns = [
    {
      title: "ประเภท",
      dataIndex: "ptype",
      key: "ptype",
      width: 220,
      render: (v, record) => (
        <Select
          style={{ width: "100%" }}
          value={v}
          options={TYPE_OPTIONS}
          onChange={(val) => updateItem(record.key, "ptype", val)}
        />
      ),
    },
    {
      title: "สินค้า",
      dataIndex: "code",
      key: "code",
      render: (v, record) => (
        <Select
          style={{ width: "100%" }}
          value={v}
          showSearch
          allowClear
          placeholder={`เลือก ${typenameOf(record.ptype)}`}
          options={optionsByType[record.ptype] || []}
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={(val) => updateItem(record.key, "code", val)}
        />
      ),
    },
    {
      title: "จำนวนที่ต้องการผลิต",
      dataIndex: "qty",
      key: "qty",
      width: 220,
      render: (v, record) => (
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          value={v}
          placeholder={record.ptype === "PG" ? "จำนวน (กล่อง)" : "จำนวน (หน่วย)"}
          formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(val) => val.replace(/,/g, "")}
          onChange={(val) => updateItem(record.key, "qty", val)}
        />
      ),
    },
    {
      title: "หน่วย",
      key: "unit",
      width: 90,
      align: "center",
      render: (_, record) =>
        record.ptype === "PG"
          ? "กล่อง"
          : (optionsByType[record.ptype] || []).find((o) => o.value === record.code)?.unit || "-",
    },
    {
      title: "",
      key: "action",
      width: 60,
      align: "center",
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={items.length <= 1}
          onClick={() => removeItem(record.key)}
        />
      ),
    },
  ];

  const ResultTitle = (label, rows) => (
    <Flex className="width-100" align="center">
      <Col span={12} className="p-0">
        <Flex gap={4} justify="start" align="center">
          <Typography.Title className="m-0 !text-zinc-800" level={3}>
            {label}
          </Typography.Title>
        </Flex>
      </Col>
      <Col span={12} style={{ paddingInline: 0 }}>
        <Flex gap={4} justify="end">
          <Button
            size="small"
            icon={<TbFileExport style={{ fontSize: ".9rem" }} />}
            className="bn-action bn-center bn-success-outline justify-center"
            onClick={() => handleExport(rows)}
          >
            <Typography.Text style={{ color: "var(--success)" }}>
              Export to Excel
            </Typography.Text>
          </Button>
        </Flex>
      </Col>
    </Flex>
  );

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Card
        title="เลือกสินค้าที่ต้องการคำนวน (PG / FG / WI)"
        extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={addItem}>
            เพิ่มรายการ
          </Button>
        }
        loading={loading}
      >
        <Table
          size="small"
          rowKey="key"
          columns={itemColumns}
          dataSource={items}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
        <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
          <Col xs={24}>
            <Flex justify="flex-end" gap={8}>
              <Button
                type="primary"
                size="small"
                className="bn-action"
                icon={<SearchOutlined />}
                onClick={handleCal}
              >
                คำนวน
              </Button>
              <Button
                type="primary"
                size="small"
                className="bn-action"
                danger
                icon={<ClearOutlined />}
                onClick={handleClear}
              >
                ล้าง
              </Button>
            </Flex>
          </Col>
        </Row>
      </Card>

      {isCalculated && (
        <>
          <Card>
            <Table
              title={() => ResultTitle("ตารางคำนวนวัถตุดิบ", resultData)}
              dataSource={resultData}
              columns={matcolumn}
              pagination={false}
              rowKey="seq"
              scroll={{ x: "max-content" }}
              size="small"
              style={{ backgroundColor: "#fafafa" }}
            />
          </Card>
          <Card>
            <Table
              title={() =>
                ResultTitle(
                  "ตารางคำนวนบรรจุภัณฑ์ (Packing)",
                  resultDataPK.filter((i) => i.typename === "Packing")
                )
              }
              dataSource={resultDataPK.filter((i) => i.typename === "Packing")}
              columns={matcolumn}
              pagination={false}
              rowKey="seq"
              scroll={{ x: "max-content" }}
              size="small"
              style={{ backgroundColor: "#fafafa" }}
            />
          </Card>
          <Card>
            <Table
              title={() =>
                ResultTitle(
                  "ตารางคำนวนภาชนะ (Packaging)",
                  resultDataPK.filter((i) => i.typename === "Packaging")
                )
              }
              dataSource={resultDataPK.filter((i) => i.typename === "Packaging")}
              columns={matcolumn}
              pagination={false}
              rowKey="seq"
              scroll={{ x: "max-content" }}
              size="small"
              style={{ backgroundColor: "#fafafa" }}
            />
          </Card>
        </>
      )}
    </Space>
  );
};

export default CalculateByProduct;
